import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Badge,
  Alert,
  AlertDescription,
} from "@/components/ui";
import { AlertTriangle, CheckCircle, Info } from "lucide-react";
import financeService from "../../services/financeService";

/**
 * Budget Selector with Balance Display
 *
 * Reusable component for selecting budgets with real-time balance information
 * Shows: Budget name, available balance, utilization percentage
 */
const BudgetSelectorWithBalance = ({
  value,
  onChange,
  label = "Budget",
  required = false,
  disabled = false,
  showBalance = true,
  filters = {},
  className = "",
}) => {
  const [budgets, setBudgets] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBudgets();
  }, []);

  useEffect(() => {
    if (value && budgets.length > 0) {
      const budget = budgets.find((b) => b.id.toString() === value.toString());
      setSelectedBudget(budget);
    } else {
      setSelectedBudget(null);
    }
  }, [value, budgets]);

  const loadBudgets = async () => {
    try {
      setLoading(true);
      const response = await financeService.getBudgetsWithBalances(filters);
      setBudgets(response?.data?.data?.budgets || []);
    } catch (error) {
      console.error("Failed to load budgets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (newValue) => {
    const budget = budgets.find((b) => b.id.toString() === newValue);
    setSelectedBudget(budget);
    onChange(newValue);
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 80) return "text-orange-600";
    if (percentage >= 60) return "text-yellow-600";
    return "text-green-600";
  };

  const getUtilizationBadge = (percentage) => {
    if (percentage >= 90)
      return { color: "bg-red-100 text-red-800", icon: AlertTriangle };
    if (percentage >= 80)
      return { color: "bg-orange-100 text-orange-800", icon: AlertTriangle };
    if (percentage >= 60)
      return { color: "bg-yellow-100 text-yellow-800", icon: Info };
    return { color: "bg-green-100 text-green-800", icon: CheckCircle };
  };

  return (
    <div className={className}>
      <div className="space-y-2">
        <Label htmlFor="budget">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>

        <Select
          value={value?.toString() || ""}
          onValueChange={handleChange}
          disabled={disabled || loading}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={loading ? "Loading budgets..." : "Select budget"}
            />
          </SelectTrigger>
          <SelectContent>
            {!required && <SelectItem value="">None</SelectItem>}
            {budgets.map((budget) => (
              <SelectItem key={budget.id} value={budget.id.toString()}>
                <div className="flex items-center justify-between w-full">
                  <span>{budget.name}</span>
                  {showBalance && (
                    <span className="ml-2 text-xs text-gray-500">
                      {budget.formatted.available} available
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Budget Balance Information */}
        {showBalance && selectedBudget && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Budget Details
                </span>
                <Badge
                  className={
                    getUtilizationBadge(selectedBudget.utilization_percentage)
                      .color
                  }
                >
                  {selectedBudget.formatted.utilization} Used
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Budgeted:</span>
                  <span className="ml-2 font-medium">
                    {selectedBudget.formatted.budgeted}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Spent:</span>
                  <span className="ml-2 font-medium">
                    {selectedBudget.formatted.actual}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Available:</span>
                  <span
                    className={`ml-2 font-bold ${getUtilizationColor(selectedBudget.utilization_percentage)}`}
                  >
                    {selectedBudget.formatted.available}
                  </span>
                </div>
              </div>

              {/* Utilization Bar */}
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      selectedBudget.utilization_percentage >= 90
                        ? "bg-red-600"
                        : selectedBudget.utilization_percentage >= 80
                          ? "bg-orange-500"
                          : selectedBudget.utilization_percentage >= 60
                            ? "bg-yellow-500"
                            : "bg-green-500"
                    }`}
                    style={{
                      width: `${Math.min(selectedBudget.utilization_percentage, 100)}%`,
                    }}
                  />
                </div>
              </div>

              {/* Warnings */}
              {selectedBudget.is_near_limit && (
                <Alert className="mt-2 bg-orange-50 border-orange-200">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800 text-xs">
                    Budget is{" "}
                    {selectedBudget.utilization_percentage >= 90
                      ? "critically low"
                      : "running low"}
                    . Only {selectedBudget.formatted.available} remaining.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* No budgets available */}
        {!loading && budgets.length === 0 && (
          <Alert className="mt-2">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              No active budgets available. Please create a budget first.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default BudgetSelectorWithBalance;
